import { DiscountUpdateEvent } from '@theme/events';

const CAMPAIGN_DURATION = 7 * 24 * 60 * 60 * 1000;

function parseCampaignDate(input) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|([+-])(\d{2}):(\d{2}))$/.exec(input || '');
  if (!match) return NaN;
  const [year, month, day, hour, minute, second] = match.slice(1, 7).map(Number);
  const zoneHours = Number(match[9] || 0);
  const zoneMinutes = Number(match[10] || 0);
  if (hour > 23 || minute > 59 || second > 59 || zoneHours > 14 || zoneMinutes > 59 || (zoneHours === 14 && zoneMinutes !== 0)) return NaN;
  const calendar = new Date(Date.UTC(year, month - 1, day));
  if (calendar.getUTCFullYear() !== year || calendar.getUTCMonth() !== month - 1 || calendar.getUTCDate() !== day) return NaN;
  return Date.parse(input);
}

function campaignWindow(config) {
  const start = parseCampaignDate(config?.start);
  const end = parseCampaignDate(config?.end);
  const valid = config?.enabled === true && config?.valid === true && Number.isFinite(start) && end - start === CAMPAIGN_DURATION;
  return { start, end, valid };
}

function campaignPhase(window, time) {
  if (!window.valid) return 'inactive';
  if (time < window.start) return 'scheduled';
  return time < window.end ? 'active' : 'expired';
}

async function waitForCartIdle() {
  if (document.documentElement.dataset.rsCartMutation !== 'active') return;
  await new Promise(resolve => {
    const observer = new MutationObserver(() => {
      if (document.documentElement.dataset.rsCartMutation === 'active') return;
      observer.disconnect();
      resolve();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-rs-cart-mutation'] });
  });
}

class FablairCampaign {
  constructor() {
    this.reload();
    this.observer = new MutationObserver(records => {
      if (records.some(record => record.addedNodes.length)) this.refresh();
    });
    this.observer.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', () => this.reload());
    document.addEventListener('visibilitychange', () => this.refresh());
    window.addEventListener('pageshow', () => this.reload());
  }

  reload() {
    window.clearInterval(this.timer);
    let config;
    try { config = JSON.parse(document.getElementById('fablair-campaign-config')?.textContent || '{}'); }
    catch { config = {}; }
    this.window = campaignWindow(config);
    this.renderedAt = Number(config.renderedAt) * 1000;
    this.loadedAt = performance.now();
    this.phase ??= campaignPhase(this.window, this.renderedAt);
    this.refresh();
    if (this.window.valid && this.now() < this.window.end) this.timer = window.setInterval(() => this.refresh(), 1000);
  }

  now() {
    const renderedClock = this.renderedAt + performance.now() - this.loadedAt;
    return Math.max(Date.now(), Number.isFinite(renderedClock) ? renderedClock : 0);
  }

  async refreshCart() {
    if (!this.cartRefreshPending || this.cartRefreshWaiting) return;
    this.cartRefreshWaiting = true;
    if (document.readyState === 'loading') {
      await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
    }
    if (!document.querySelector('cart-items-component')) {
      this.cartRefreshWaiting = false;
      return;
    }
    await customElements.whenDefined('cart-items-component');
    await waitForCartIdle();
    this.cartRefreshWaiting = false;
    if (!this.cartRefreshPending) return;
    this.cartRefreshPending = false;
    document.dispatchEvent(new DiscountUpdateEvent(null, 'fablair-campaign'));
  }

  refresh() {
    const { start, end, valid } = this.window;
    const now = this.now();
    const active = valid && now >= start && now < end;
    const phase = campaignPhase(this.window, now);
    if (phase !== this.phase) {
      this.phase = phase;
      this.cartRefreshPending = true;
    }
    this.refreshCart();
    const remaining = active ? Math.ceil((end - now) / 1000) : 0;
    const values = { days: Math.floor(remaining / 86400), hours: Math.floor(remaining / 3600) % 24, minutes: Math.floor(remaining / 60) % 60, seconds: remaining % 60 };
    for (const element of document.querySelectorAll('[data-fablair-campaign]')) {
      if (element.hidden === active) element.hidden = !active;
      if (!active || element.dataset.campaignKind !== 'countdown') continue;
      for (const [name, value] of Object.entries(values)) {
        const number = element.querySelector(`[data-${name}]`);
        const text = String(value).padStart(2, '0');
        if (number && number.textContent !== text) number.textContent = text;
      }
    }
    for (const section of document.querySelectorAll('[data-fablair-campaign-section]')) {
      const sectionActive = active || section.dataset.campaignPreview === 'true';
      section.classList.toggle('rs-campaign--inactive', !sectionActive);
      const grid = section.querySelector('[data-fablair-campaign-grid]');
      if (grid) grid.style.setProperty('--rs-campaign-columns', sectionActive ? grid.dataset.activeColumns : grid.dataset.inactiveColumns);
    }
    if (valid && now >= end) window.clearInterval(this.timer);
  }
}

new FablairCampaign();
